import React, { useState } from "react";
import { toast } from "sonner";
import { errMsg, fmt, useApi, useLoad, useTaxonomy } from "../../lib/api";
import { Empty, ErrorBox, Loading, OrganicTag, SponsoredTag, StatusBadge } from "../../components/kit";

import { FilterBar, qs } from "./OpsOrgAdmin";

export function OpsAudit() {
  const [outcome, setOutcome] = useState("");
  const { data, loading, error, reload } = useLoad<any[]>(`/ops/audit?limit=200${outcome ? `&outcome=${outcome}` : ""}`);
  return (
    <div className="card overflow-x-auto">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label htmlFor="audit-outcome" className="label !mb-0">Outcome</label>
        <select id="audit-outcome" className="input !w-auto" value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="ops-audit-outcome-select">
          <option value="">All</option><option value="allow">Allowed</option><option value="deny">Denied</option>
        </select>
        <button className="btn btn-sm btn-ghost" onClick={reload} data-testid="ops-audit-refresh-button">Refresh</button>
      </div>
      {loading && !data && <Loading />}
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data?.length === 0 && <Empty title="No audit events" />}
      {!!data?.length && (
        <table className="table mono text-xs" data-testid="ops-audit-table">
          <thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Resource</th><th>Outcome</th><th>Reason</th></tr></thead>
          <tbody>
            {data.map((a) => (
              <tr key={a.id}><td>{fmt(a.at)}</td><td>{a.actor_kind}</td><td>{a.action}</td><td>{a.resource_type}:{(a.resource_id || "").slice(0, 10)}</td><td><StatusBadge value={a.outcome} /></td><td>{a.reason}</td></tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const Counts = ({ title, obj }: { title: string; obj: Record<string, number> }) => (
  <div className="card">
    <h3 className="text-xl font-bold">{title}</h3>
    <dl className="mt-2 space-y-1 text-sm">
      {Object.keys(obj).length === 0 && <p className="text-[var(--muted)]">No data</p>}
      {Object.entries(obj).map(([k, v]) => <div key={k} className="flex justify-between"><dt>{k.replace(/_/g, " ")}</dt><dd className="mono font-semibold">{v}</dd></div>)}
    </dl>
  </div>
);

export function OpsAnalytics() {
  const { data, loading, error, reload } = useLoad<any>("/ops/analytics");
  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  if (!data) return null;
  return (
    <div className="space-y-8">
      <section className="organic-zone" data-testid="ops-analytics-organic">
        <div className="mb-3"><OrganicTag text="Non-private marketplace analytics" /></div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Counts title="Events" obj={data.organic.events} />
          <Counts title="Requests" obj={data.organic.requests_by_status} />
          <Counts title="Introductions" obj={data.organic.introductions_by_status} />
          <Counts title="Consents" obj={data.organic.consents_by_status} />
        </div>
      </section>
      <section className="sponsored-zone overflow-x-auto" data-testid="ops-analytics-sponsored">
        <div className="mb-3"><SponsoredTag /> <span className="text-xs">Tracked separately from organic analytics</span></div>
        <table className="table">
          <thead><tr><th>Ad</th><th>Org</th><th>Status</th><th>Impressions</th><th>Clicks</th></tr></thead>
          <tbody>{data.sponsored.map((a: any) => <tr key={a.id}><td>{a.title}</td><td>{a.org_name}</td><td><StatusBadge value={a.status} /></td><td className="mono">{a.impressions}</td><td className="mono">{a.clicks}</td></tr>)}</tbody>
        </table>
      </section>
    </div>
  );
}

function MatchPreview({ id }: { id: string }) {
  const { data, loading, error } = useLoad<any>(`/ops/requests/${id}/match-preview`);
  if (loading) return <Loading />;
  if (error) return <ErrorBox message={error} />;
  const row = (c: any) => <li key={c.org_id} className="mono text-xs">#{c.rank} {c.display_name} · fit {c.fit} · exp {c.exposures_at_match}</li>;
  return (
    <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid={`ops-match-preview-${id}`}>
      <div><p className="label">Stored at match (v{data.stored?.request_version})</p><ol>{data.stored?.candidates.map(row)}</ol></div>
      <div><p className="label">Live recomputation</p><ol>{data.live_preview.map(row)}</ol></div>
      <p className="hint sm:col-span-2">Basis: {data.stored?.basis}. Subscription tier and ads are not inputs.</p>
    </div>
  );
}

export function OpsRequests() {
  const [f, setF] = useState({ q: "", status: "", category_id: "", flagged: "" });
  const { data, loading, error, reload } = useLoad<any[]>(`/ops/requests?${qs(f)}`);
  const { catName, areaName, categories } = useTaxonomy();
  const api = useApi();
  const [open, setOpen] = useState<string | null>(null);
  const act = async (rid: string, action: string) => {
    const reason = action === "unflag" ? "" : window.prompt(`Reason to ${action} this request (non-private, shown in audit):`) ?? null;
    if (reason === null) return;
    try { await api("post", `/ops/requests/${rid}/status`, { action, reason }); toast.success(`Request ${action === "close" ? "closed" : action + "ged"}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const bar = (
    <FilterBar>
      <input aria-label="Search request titles" placeholder="Search title…" className="input !w-auto flex-1" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="ops-requests-search-input" />
      <select aria-label="Request status" className="input !w-auto" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="ops-requests-status-filter">
        <option value="">All statuses</option><option value="open">Open</option><option value="closed">Closed</option><option value="cancelled">Cancelled</option>
      </select>
      <select aria-label="Trade" className="input !w-auto" value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })} data-testid="ops-requests-category-filter">
        <option value="">All trades</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={f.flagged === "true"} onChange={(e) => setF({ ...f, flagged: e.target.checked ? "true" : "" })} data-testid="ops-requests-flagged-filter" />Flagged only</label>
    </FilterBar>
  );
  if (loading && !data) return <>{bar}<Loading /></>;
  if (error) return <>{bar}<ErrorBox message={error} onRetry={reload} /></>;
  if (!data?.length) return <>{bar}<Empty title="No requests match" /></>;
  return (
    <>{bar}
    <ul className="space-y-2" data-testid="ops-requests-list">
      {data.map((r) => (
        <li key={r.id} className="card text-sm" data-testid={`ops-request-${r.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><strong>{r.title}</strong> <span className="text-[var(--muted)]">· {catName(r.category_id)} · {areaName(r.area_id)} · v{r.version}</span></div>
            <div className="flex flex-wrap items-center gap-1">{r.flagged && <span className="badge badge-red">flagged</span>}<StatusBadge value={r.status} />{r.introduction_statuses.map((s: string, i: number) => <StatusBadge key={i} value={s} />)}</div>
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            <button className="btn btn-sm btn-ghost" onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`ops-match-preview-button-${r.id}`}>{open === r.id ? "Hide" : "Match preview"}</button>
            <button className="btn btn-sm btn-ghost" onClick={() => act(r.id, r.flagged ? "unflag" : "flag")} data-testid={`ops-request-flag-${r.id}`}>{r.flagged ? "Unflag" : "Flag"}</button>
            {r.status === "open" && <button className="btn btn-sm btn-danger" onClick={() => act(r.id, "close")} data-testid={`ops-request-close-${r.id}`}>Close request</button>}
          </div>
          {open === r.id && <MatchPreview id={r.id} />}
        </li>
      ))}
    </ul>
    </>
  );
}

function TaxRow({ t, kind, run, api }: any) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(t.name);
  const [kw, setKw] = useState((t.keywords || []).join(", "));
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await run(() => api("patch", `/ops/${kind}/${t.id}`, { name, keywords: kw.split(",") }), "Saved");
    if (ok) setEdit(false);
  };
  if (edit) {
    return (
      <li><form onSubmit={save} className="space-y-2 rounded border border-[var(--blue)] p-2" data-testid={`ops-${kind}-edit-${t.id}`}>
        <input aria-label="Name" required minLength={2} className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid={`ops-${kind}-name-input-${t.id}`} />
        <input aria-label="Keyword synonyms, comma separated" placeholder="Keyword synonyms, comma separated" className="input" value={kw} onChange={(e) => setKw(e.target.value)} data-testid={`ops-${kind}-keywords-input-${t.id}`} />
        <div className="flex gap-2"><button className="btn btn-sm btn-primary" data-testid={`ops-${kind}-save-${t.id}`}>Save</button><button type="button" className="btn btn-sm btn-ghost" onClick={() => setEdit(false)}>Cancel</button></div>
      </form></li>
    );
  }
  return (
    <li className="flex items-center justify-between gap-2">
      <span className={t.active ? "" : "line-through text-[var(--muted)]"}>{t.name}{t.keywords?.length ? <span className="block text-xs text-[var(--muted)]">{t.keywords.join(", ")}</span> : null}</span>
      <span className="flex gap-1">
        <button className="btn btn-sm btn-ghost" onClick={() => setEdit(true)} data-testid={`ops-${kind}-rename-${t.id}`}>Edit</button>
        <button className="btn btn-sm btn-ghost" onClick={() => run(() => api("patch", `/ops/${kind}/${t.id}`, { active: !t.active }), "Updated")} data-testid={`ops-${kind}-toggle-${t.id}`}>{t.active ? "Deactivate" : "Activate"}</button>
      </span>
    </li>
  );
}

function TaxList({ title, items, kind, reload }: { title: string; items: any[]; kind: string; reload: () => void }) {
  const api = useApi();
  const [name, setName] = useState("");
  const run = async (fn: () => Promise<any>, ok: string) => { try { await fn(); toast.success(ok); reload(); return true; } catch (e) { toast.error(errMsg(e)); return false; } };
  return (
    <div className="card space-y-3">
      <h3 className="text-2xl font-bold">{title}</h3>
      <ul className="space-y-1 text-sm">
        {items.map((t) => <TaxRow key={t.id} t={t} kind={kind} run={run} api={api} />)}
      </ul>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); run(() => api("post", `/ops/${kind}`, { name }), "Added").then((done) => done && setName("")); }}>
        <input aria-label={`New ${kind}`} required minLength={2} className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid={`ops-${kind}-new-input`} />
        <button className="btn btn-dark" data-testid={`ops-${kind}-add-button`}>Add</button>
      </form>
    </div>
  );
}

export function OpsTaxonomy() {
  const { data, loading, error, reload } = useLoad<any>("/ops/taxonomy");
  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  if (!data) return null;
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <TaxList title="Trades" items={data.categories} kind="categories" reload={reload} />
      <TaxList title="Service areas" items={data.areas} kind="areas" reload={reload} />
    </div>
  );
}
